import orderBy from 'lodash/orderBy'

import {
  Badge,
  CalendarCourtReporterAdminEvent,
  CourtReporterSource,
  EventFormat,
  ReporterSilo,
} from '@/apiClient'
import { AttendeeRole, EventType } from '@/constants'
import {
  AdminEvent,
  Attendee,
  Event,
  Reporter,
  ReporterBase,
  ReporterEvent,
} from '@/types'
import { STATES } from '@/utils/states'

// duplicated in parrot backend parrot/external/models/reporter.py
const STATES_WITHOUT_COMMISSION_NUMBER = ['MA', 'MI']

export const isReporterEvent = (event: Event): event is ReporterEvent =>
  'reporter_status' in event

export const isAdminEvent = (event: Event): event is AdminEvent =>
  'reporter' in event

export const isCommissionNumberRequired = (
  reporter_state: string | undefined,
): boolean =>
  !reporter_state || !STATES_WITHOUT_COMMISSION_NUMBER.includes(reporter_state)

export const isWithPreferredReporter = (
  event: Pick<Event, 'reporter_silo' | 'court_reporter_source'>,
): boolean =>
  event.reporter_silo === ReporterSilo.court_reporter &&
  event.court_reporter_source === CourtReporterSource.requested

export const isWithClientReporter = (
  event: Pick<Event, 'reporter_silo' | 'court_reporter_source'>,
): boolean =>
  event.reporter_silo === ReporterSilo.court_reporter &&
  event.court_reporter_source === CourtReporterSource.client_own

export const getEventFormat = (
  event: Pick<CalendarCourtReporterAdminEvent, 'event_format'>,
): EventFormat => event.event_format ?? EventFormat.remote

export const isWitnessLocationRequired = (
  event: Pick<Event, 'case'>,
): boolean => event.case.state === 'AZ'

export const isWithSessionAssistant = (
  event: Pick<Event, 'reporter_silo'>,
): boolean => event.reporter_silo === ReporterSilo.court_reporter

// Single source of truth for whether the transcript-order input is shown on the feedback
// form (FeedbackWitnessForm) - also used to skip transcript_ordered validation for events
// where the SA has no way to satisfy it.
export const shouldProvideTranscriptOrders = (
  event: Pick<Event, 'type' | 'reporter_silo' | 'court_reporter_source'>,
): boolean =>
  event.type !== EventType.EXAMINATION_UNDER_OATH &&
  event.type !== EventType.HEARING &&
  !isWithClientReporter(event)

// Whether the SA script takes transcript orders. A client's own Court Reporter produces
// the transcript, so it can't be ordered through Parrot.
export const shouldSaTakeTranscriptOrders = (
  event: Pick<Event, 'reporter_silo' | 'court_reporter_source'>,
): boolean => !isWithClientReporter(event)

// The role the booked reporter (the logged-in reporter running the script) is
// added with: Session Assistant on Court Reporter events, otherwise Digital
// Reporter. CR events also have a separate, external `court_reporter` attendee.
export const getBookedReporterRole = (
  event: Pick<Event, 'reporter_silo'>,
): AttendeeRole =>
  isWithSessionAssistant(event)
    ? AttendeeRole.SESSION_ASSISTANT
    : AttendeeRole.DIGITAL_REPORTER

// Whether the booked reporter is already present. Scoped to the exact role the
// add path uses so a separate `court_reporter` attendee on a CR event does not
// suppress adding the Session Assistant.
export const isBookedReporterInAttendees = (
  attendees: { role: AttendeeRole }[],
  event: Pick<Event, 'reporter_silo'>,
): boolean => attendees.some(a => a.role === getBookedReporterRole(event))

export const getCrucialFields = (reporter: Reporter) => {
  const crucialFields = ['phone number', 'state']

  if (reporter.requires_commission_number) {
    crucialFields.push('commission number')
  } else {
    crucialFields.push('county')
  }

  crucialFields.push('commission expiration date', 'commission license')

  return crucialFields
}

export const getUnifiedEventStatus = (
  event: ReporterEvent | Event,
): ReporterEvent['reporter_status'] | Event['general_status'] => {
  if (isReporterEvent(event)) {
    return event.reporter_status
  } else {
    return event.general_status
  }
}

export const isReporterAndEventStateEqual = (
  event: { case: { state?: string | null } },
  reporter: { state?: string | null },
) => {
  return reporter.state === event.case.state
}

export const getAttorneys = <
  A extends Pick<Attendee, 'is_attorney' | 'is_attorney_who_is_our_customer'>,
>(event: {
  attendees: A[]
}): A[] =>
  orderBy(
    event.attendees.filter(attendee => attendee.is_attorney),
    ['is_attorney_who_is_our_customer'],
    ['desc'],
  )

export const getAttendeeRolesForEvent = (
  event: Pick<Event, 'type'>,
): AttendeeRole[] => {
  const getRolesByType = (eventType: EventType): AttendeeRole[] => {
    if (eventType === EventType.DEPOSITION) {
      return [
        AttendeeRole.DEFENDANT_ATTORNEY,
        AttendeeRole.PLAINTIFF_ATTORNEY,
        AttendeeRole.WITNESS_ATTORNEY,
        AttendeeRole.WITNESS,
        AttendeeRole.INTERPRETER,
        AttendeeRole.GUARDIAN,
        AttendeeRole.OTHER,
      ]
    } else if (eventType === EventType.EXAMINATION_UNDER_OATH) {
      return [
        AttendeeRole.INTERPRETER,
        AttendeeRole.INVESTIGATOR,
        AttendeeRole.OPPOSING_COUNSEL,
        AttendeeRole.STAFF_COUNSEL,
        AttendeeRole.WITNESS_ATTORNEY,
        AttendeeRole.WITNESS,
        AttendeeRole.GUARDIAN,
        AttendeeRole.OTHER,
      ]
    } else if (eventType === EventType.HEARING) {
      return [
        AttendeeRole.DEFENDANT_ATTORNEY,
        AttendeeRole.PLAINTIFF_ATTORNEY,
        AttendeeRole.CLERK_OF_COURT,
        AttendeeRole.INTERPRETER,
        AttendeeRole.JUDGE,
        AttendeeRole.WITNESS_ATTORNEY,
        AttendeeRole.WITNESS,
        AttendeeRole.OTHER,
      ]
    } else if (eventType === EventType.MEET_AND_CONFER) {
      return [AttendeeRole.DEFENDANT_ATTORNEY, AttendeeRole.PLAINTIFF_ATTORNEY]
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _exhaustiveCheck: never = eventType

    return []
  }

  const reporterRoles = [
    AttendeeRole.DIGITAL_REPORTER,
    AttendeeRole.VIDEOGRAPHER,
    AttendeeRole.COURT_REPORTER,
    AttendeeRole.SESSION_ASSISTANT,
  ]

  return [...getRolesByType(event.type), ...reporterRoles]
}

export const LEVELS = [
  { label: 'Onboarding', value: '0' },
  { label: 'Level 1', value: '1' },
  { label: 'Level 2', value: '2' },
  { label: 'Level 3', value: '3' },
  { label: 'Level 4', value: '4' },
  { label: 'Level 5', value: '5' },
]

// Onboarding is set through the legacy override, as in `event_status.py`.
export const isOnboardingEvent = (
  event: Pick<Event, 'computed_required_reporter_experience_level'>,
): boolean => event.computed_required_reporter_experience_level === 0

// `null` = parrot has not scored the event.
export const eventLevel = (
  event: Pick<
    Event,
    'computed_required_reporter_experience_level' | 'event_level'
  >,
): number | null => (isOnboardingEvent(event) ? 0 : event.event_level)

export const getEventLevelLabel = (level: number): string => {
  if (level === 0) {
    return 'Onboarding'
  }
  if (level <= 1) {
    return 'Beginner'
  }
  if (level <= 3) {
    return 'Intermediate'
  }
  return 'Expert'
}

// The tier the reporter booked, when it reads differently from the event's
// tier now. The labels bucket the tiers the way the payout rates do, so a move
// inside one bucket changes neither what the reporter sees nor what they are
// paid.
export const bookedEventLevelLabel = (
  event: Pick<
    Event,
    | 'computed_required_reporter_experience_level'
    | 'event_level'
    | 'event_level_at_booking'
  > & { reporter_status?: ReporterEvent['reporter_status'] },
): string | null => {
  const booked = event.event_level_at_booking

  if (
    booked === null ||
    event.event_level === null ||
    isOnboardingEvent(event) ||
    // A DR sees `unavailable` for an event another DR booked. Ops has no
    // `reporter_status`, and sees every booking.
    event.reporter_status === 'unavailable'
  ) {
    return null
  }

  const label = getEventLevelLabel(booked)

  return label === getEventLevelLabel(event.event_level) ? null : label
}

export const BADGE_LABELS: Record<Badge, string> = {
  session_assistant: 'Session Assistant',
  dia: 'Depositions in Aid',
  multi_witness: 'Multi-Witness',
  on_call: 'On-Call',
  washington_dr: 'Washington DR',
  california_dr: 'California DR',
}

export const getBadgeLabel = (badge: Badge): string =>
  BADGE_LABELS[badge] ?? badge

export const getReporterLabel = (
  reporter: ReporterBase,
  config: { showLevel: boolean; showState: boolean },
): string => {
  const label: string[] = []

  if (config.showLevel) {
    label.push(`L${reporter.level}`)
  }
  if (config.showState) {
    label.push(reporter.state !== undefined ? STATES[reporter.state] : '--')
  }

  return `${reporter.name}` + (label.length > 0 ? ` (${label.join(' ')})` : '')
}
